import type { AuditScoreResult, CoverageLevel, HealthGrade, ScoreMode } from '@/types/gmb';

/** 計分流程狀態（idle = 尚未觸發，不顯示） */
export type ScoreStatus = 'idle' | 'loading' | 'success' | 'error';

interface ScoreSummarySectionProps {
  status: ScoreStatus;
  result: AuditScoreResult | null;
  errorMessage: string | null;
  /** 計分失敗時重新嘗試（不重新取得 Google 資料） */
  onRetry: () => void;
}

const COVERAGE_LABEL: Record<CoverageLevel, string> = {
  COMPLETE: '本次評分資料完整',
  GOOD: '本次評分資料良好',
  LIMITED: '本次評分資料有限',
  INSUFFICIENT: '本次評分資料不足',
};

/** Backend healthLabel 優先；缺失時才用此對照（仍不重算 Grade）。 */
const GRADE_FALLBACK_LABEL: Record<HealthGrade, string> = {
  A: '優良',
  B: '良好',
  C: '尚可',
  D: '待加強',
  E: '優先改善',
};

const GRADE_STYLE: Record<HealthGrade, string> = {
  A: 'bg-primary-100 text-primary-900 ring-primary-200',
  B: 'bg-primary-50 text-primary-800 ring-primary-200',
  C: 'bg-accent-100 text-accent-900 ring-accent-200',
  D: 'bg-accent-200 text-accent-900 ring-accent-300',
  E: 'bg-rose-100 text-rose-800 ring-rose-200',
};

const SCORE_MODE_LABEL: Record<ScoreMode, string> = {
  FULL: '完整健診',
  PARTIAL: '暫定結果',
  INSUFFICIENT: '資料不足',
  CLOSED: '已歇業',
};

function ScoreModeBadge({ mode }: { mode: ScoreMode }) {
  if (mode === 'PARTIAL') {
    return (
      <span className="ds-badge bg-accent-100 text-accent-900 ring-1 ring-accent-200">
        <i className="ri-time-line" />
        {SCORE_MODE_LABEL.PARTIAL}
      </span>
    );
  }
  if (mode === 'INSUFFICIENT') {
    return (
      <span className="ds-badge bg-secondary-100 text-secondary-800 ring-1 ring-secondary-200">
        <i className="ri-information-line" />
        {SCORE_MODE_LABEL.INSUFFICIENT}
      </span>
    );
  }
  if (mode === 'CLOSED') {
    return (
      <span className="ds-badge bg-rose-100 text-rose-800 ring-1 ring-rose-200">
        <i className="ri-close-circle-line" />
        {SCORE_MODE_LABEL.CLOSED}
      </span>
    );
  }
  return (
    <span className="ds-badge bg-primary-100 text-primary-900 ring-1 ring-primary-200">
      <i className="ri-checkbox-circle-line" />
      {SCORE_MODE_LABEL.FULL}
    </span>
  );
}

function CoverageRow({ result }: { result: AuditScoreResult }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-base text-foreground-700">
      <span className="inline-flex items-center gap-2">
        <i className="ri-database-2-line text-lg text-primary-700" />
        評分資料覆蓋率 <strong className="text-lg text-foreground-950">{result.dataCoveragePercent}%</strong>
      </span>
      <span className="ds-badge bg-primary-50 text-primary-800 ring-1 ring-primary-200">
        {COVERAGE_LABEL[result.coverageLevel]}
      </span>
    </div>
  );
}

function GradeBadge({ result }: { result: AuditScoreResult }) {
  if (!result.healthGrade) return null;
  const label = result.healthLabel || GRADE_FALLBACK_LABEL[result.healthGrade];
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-base font-bold ring-1 ${GRADE_STYLE[result.healthGrade]}`}
    >
      <span className="text-lg leading-none">{result.healthGrade}</span>
      <span className="leading-none">{label}</span>
    </span>
  );
}

/**
 * Google 商家健康度摘要（Batch 6-B1）。
 * 只顯示總分 / ScoreMode / Grade / 覆蓋率；11 項明細與 Top 3 Priority 留報告。
 */
export default function ScoreSummarySection({
  status,
  result,
  errorMessage,
  onRetry,
}: ScoreSummarySectionProps) {
  if (status === 'idle') return null;

  if (status === 'loading') {
    return (
      <section className="ds-container pt-8">
        <div className="rounded-2xl border border-primary-200 bg-background-50 p-5 md:p-6">
          <div className="flex items-center gap-4">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-700" />
            <div className="min-w-0">
              <p className="text-base font-bold text-foreground-900">正在產生健診結果…</p>
              <p className="mt-1 text-base text-foreground-600">依目前取得的公開資料與同業樣本進行計分。</p>
            </div>
          </div>
          <div className="mt-5 h-14 w-40 animate-pulse rounded-xl bg-background-200" />
        </div>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="ds-container pt-8">
        <div className="flex items-start gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800 md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50/80">
            <i className="ri-error-warning-line text-xl" />
          </span>
          <div className="min-w-0">
            <p className="text-base font-bold md:text-lg">無法完成健康度計分</p>
            <p className="mt-1.5 text-base leading-relaxed opacity-90">
              {errorMessage || '健診分數計算暫時無法完成，請稍後再試。'}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-rose-300 bg-background-50 px-4 py-2.5 text-base font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              <i className="ri-refresh-line text-lg" />
              重新計算
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!result) return null;

  const isClosed = result.scoreMode === 'CLOSED';
  const isInsufficient = !isClosed && (result.scoreMode === 'INSUFFICIENT' || result.score === undefined);

  return (
    <section className="ds-container pt-8">
      <div className="rounded-2xl border border-primary-200 bg-background-50 p-5 shadow-card md:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-200">
              <i className="ri-heart-pulse-line text-xl" />
            </span>
            <div>
              <p className="text-base font-bold text-foreground-950 md:text-lg">Google 商家健康度</p>
              <p className="text-sm text-foreground-500">天地人網 Google 商家公開資料健康度</p>
            </div>
          </div>
          <ScoreModeBadge mode={result.scoreMode} />
        </div>

        {isInsufficient ? (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4 text-accent-900 md:p-5">
            <i className="ri-alert-line mt-0.5 text-xl" />
            <div className="min-w-0">
              <p className="text-base font-bold">目前資料不足，暫時無法產生可靠的健康度分數。</p>
              <p className="mt-2 text-base">
                評分資料覆蓋率為 {result.dataCoveragePercent}%（{COVERAGE_LABEL[result.coverageLevel]}
                ），需更多可比較資料才能計分。
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-end gap-x-5 gap-y-3">
              <div className="flex items-end gap-2">
                <span className="text-[3.5rem] font-bold leading-none text-primary-900 md:text-[4rem]">
                  {result.score}
                </span>
                <span className="pb-2 text-lg font-medium text-foreground-400">/ 100</span>
              </div>
              <GradeBadge result={result} />
            </div>

            <div className="mt-6">
              <CoverageRow result={result} />
            </div>

            {isClosed && (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
                <i className="ri-close-circle-line mt-0.5 text-xl" />
                <p className="text-base font-semibold">Google 商家目前顯示永久歇業。</p>
              </div>
            )}

            {result.scoreMode === 'PARTIAL' && (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4 text-accent-900">
                <i className="ri-information-line mt-0.5 text-xl" />
                <p className="text-base">
                  部分公開資料或同業比較資料不足，此分數依目前可取得資料計算，屬暫定結果。
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}